import Button from "@material-ui/core/Button";
import Grid, { GridSize, GridSpacing } from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import { Theme, WithStyles, createStyles, withStyles } from "@material-ui/core/styles";
import { Variant } from "@material-ui/core/styles/createTypography";
import AddIcon from "@material-ui/icons/Add";
import { ArrayFieldTemplateProps as RjsfArrayFieldTemplateProps } from "@rjsf/core";
import classNames from "classnames";
import React from "react";
import { JsonSchema } from "../../models";
import { Description, Title } from "../shared";
import { ArrayFieldTemplateItem } from "./ArrayFieldTemplateItem";

const styles = (theme: Theme) =>
  createStyles({
    items: {},
    objectItems: {},
    enumeration: {
      display: "flex",
      "& > div": {
        width: "100%",
      },
      "& > .enumeration-number": {
        width: "auto",
        flexBasis: "2rem",
        flexGrow: 0,
        flexShrink: 0,
        paddingTop: 38,
        fontSize: theme.typography.body1.fontSize,
      },
    },
    addButton: {
      marginTop: theme.spacing(2),
      alignSelf: "flex-start",
    },
  });

interface ArrayFieldTemplateOptions {
  title?: {
    variant?: Variant | "label";
    margin?: "normal" | "none";
  };
  layout?: {
    spacing?: GridSpacing;
    size?: {
      xs?: GridSize;
      sm?: GridSize;
      md?: GridSize;
      lg?: GridSize;
      xl?: GridSize;
    };
    enumerated?: boolean;
  };
  emptyValue?: string;
}

interface ArrayFieldTemplateProps extends RjsfArrayFieldTemplateProps, WithStyles<typeof styles> {}

export const ArrayFieldTemplateComponent = (props: ArrayFieldTemplateProps) => {
  const { idSchema, schema, uiSchema, disabled, readonly, title, items, canAdd, classes, onAddClick } = props;

  const description = uiSchema["ui:description"] ?? schema.description;

  const uiOptions = uiSchema["ui:options"] as ArrayFieldTemplateOptions | undefined;

  const titleMargin = uiOptions?.title?.margin;
  const titleVariant = uiOptions?.title?.variant;

  const gridSpacing = uiOptions?.layout?.spacing;

  const gridSizeXs = uiOptions?.layout?.size?.xs || 12;
  const gridSizeSm = uiOptions?.layout?.size?.sm;
  const gridSizeMd = uiOptions?.layout?.size?.md;
  const gridSizeLg = uiOptions?.layout?.size?.lg;
  const gridSizeXl = uiOptions?.layout?.size?.xl;

  const isEnumerated = uiOptions?.layout?.enumerated === true;

  const emptyValue = uiOptions?.emptyValue ?? "(None)";

  const isObjectItems = (schema.items as JsonSchema).type === "object";

  return (
    <>
      {title && <Title fieldId={`${idSchema.$id}_title`} title={title} margin={titleMargin} variant={titleVariant} />}
      {description && <Description fieldId={`${idSchema.$id}_description`} description={description} />}
      <Grid
        container={true}
        spacing={gridSpacing}
        className={classNames(classes.items, isObjectItems && classes.objectItems)}
      >
        {items.length ? (
          items.map((item, index) =>
            isEnumerated ? (
              <Grid item={true} xs={12} key={`item-${index}`} className={classes.enumeration}>
                <div className="enumeration-number">{index + 1}.</div>
                <ArrayFieldTemplateItem {...item} />
              </Grid>
            ) : (
              <Grid
                item={true}
                xs={gridSizeXs}
                sm={gridSizeSm}
                md={gridSizeMd}
                lg={gridSizeLg}
                xl={gridSizeXl}
                key={`item-${index}`}
              >
                <ArrayFieldTemplateItem {...item} />
              </Grid>
            ),
          )
        ) : emptyValue ? (
          <Grid item={true} xs={12}>
            <Typography color="textPrimary">{emptyValue}</Typography>
          </Grid>
        ) : null}
      </Grid>
      {canAdd && (
        <Button
          variant="contained"
          color="primary"
          className={classes.addButton}
          disabled={disabled || readonly}
          onClick={onAddClick}
        >
          <AddIcon />
          &nbsp;&nbsp;Add
        </Button>
      )}
    </>
  );
};

export const ArrayFieldTemplate = withStyles(styles)(ArrayFieldTemplateComponent);
